import RichtextAdapter from "modules/storyblok/adapters/RichtextAdapter";
import { DynamicComponent } from "modules/storyblok/DynamicComponent";
import isEmpty from "modules/storyblok/adapters/RichtextAdapter/utils/isEmpty";
import type { Richtext } from "storyblok-js-client";
import { LinkIcon } from "styles/icons";
import { useScrollContext } from "contexts/ScrollContext";
import { useWindowSize } from "hooks/useWindowSize";
import { ChakraImage, ChakraFlex as Flex, ChakraBox as Box, ChakraLink as Link } from "@airhelp/react-ui-components";
import type { ImageAdapterProps } from "../../modules/storyblok/adapters/Image";
import { imageStyles, boxStyles, imageCreditStyles } from "./Image.styles";
import type { ImageProps } from "./Image.d";

const renderImageContent = (content: ImageAdapterProps["content"]) =>
  content &&
  content.map((item) => (
    <Box key={item._uid} sx={{ ...boxStyles }}>
      <DynamicComponent blok={item} />
    </Box>
  ));

const renderImageCredit = (credit: Richtext) =>
  !isEmpty(credit) && (
    <Flex mt={3}>
      <LinkIcon color="greyscale.500" w={5} h={5} mr={3} />
      <RichtextAdapter content={credit} customStyles={imageCreditStyles} />
    </Flex>
  );

const Image = ({ srcDesktop, srcMobile, alt, height, link, content, credit }: ImageProps) => {
  const windowSize = useWindowSize();
  const { isAnchorInView, setAnchorInView } = useScrollContext();

  const scrollToAnchor = () => {
    if (isAnchorInView) return;

    const anchorId = window.location.hash.slice(1);
    const anchorElement = anchorId && document.getElementById(anchorId);

    if (anchorElement && !isAnchorInView) {
      const scrollToAnchor = () => {
        anchorElement.scrollIntoView();
        setAnchorInView(true);
      };
      requestAnimationFrame(scrollToAnchor);
    }
  };

  if (!windowSize) return null;

  const { isTablet } = windowSize;

  const imageProps = {
    alt,
    height,
    sx: imageStyles,
    loading: "lazy" as const,
    src: (isTablet ? srcMobile : srcDesktop) || srcDesktop,
    onLoad: scrollToAnchor,
  };

  return link ? (
    <Link href={link} isExternal>
      <ChakraImage {...imageProps} cursor="true" />
      {renderImageContent(content)}
      {renderImageCredit(credit)}
    </Link>
  ) : (
    <Box pos="relative">
      <ChakraImage {...imageProps} />
      {renderImageContent(content)}
      {renderImageCredit(credit)}
    </Box>
  );
};

export default Image;
